import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Link, Chip, Collapse, Tooltip } from '@mui/material';
import { OpenInNew, InsertDriveFile } from '@mui/icons-material';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { getApiUrl } from '../config';

const PanelRoot = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.surface.inset,
    minWidth: 0,
}));

const PanelHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
}));

const PanelTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
}));

const TaskNumberChip = styled(Chip)(({ theme }) => ({
    height: 20,
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightMedium,
}));

const ExternalLink = styled(Link)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    wordBreak: 'break-word',
}));

const LinkIcon = styled(OpenInNew)(({ theme }) => ({
    fontSize: `${theme.typography.size.md} !important`,
}));

const PanelGrid = styled(Box, { shouldForwardProp: (prop) => prop !== '$split' })(({ theme, $split }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: $split ? 'minmax(0, 260px) minmax(0, 1fr)' : 'minmax(0, 1fr)',
        gap: theme.spacing(2),
        alignItems: 'start',
    },
}));

const ScreenshotButton = styled('button')(({ theme }) => ({
    display: 'block',
    width: '100%',
    padding: 0,
    cursor: 'pointer',
    lineHeight: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.surface.track,
    transition: 'border-color 120ms ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },
}));

const ScreenshotImage = styled('img')({
    display: 'block',
    width: '100%',
    height: 'auto',
    maxHeight: 260,
    objectFit: 'contain',
});

const ScreenshotFallback = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(1),
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const Details = styled(Box)({
    minWidth: 0,
});

const SubLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.75),
}));

const Field = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(0.25),
    marginBottom: theme.spacing(0.75),
    minWidth: 0,
    [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: 'minmax(0, 116px) minmax(0, 1fr)',
        gap: theme.spacing(1),
        alignItems: 'baseline',
    },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const FieldValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    wordBreak: 'break-word',
    minWidth: 0,
}));

const EmailLink = styled(Link)(({ theme }) => ({
    display: 'block',
    fontSize: theme.typography.size.sm,
    wordBreak: 'break-all',
}));

const CodeLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
}));

const CodeBlock = styled(Box)(({ theme }) => ({
    margin: 0,
    padding: theme.spacing(0.75, 1),
    maxHeight: 104,
    overflow: 'auto',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: theme.typography.size.xs,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.75),
}));

const RevealToggle = styled(Link)(({ theme }) => ({
    display: 'inline-block',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: theme.typography.size.sm,
    marginBottom: theme.spacing(0.75),
}));

const TagRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
}));

const AttachmentGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const AttachmentThumbButton = styled('button')(({ theme }) => ({
    display: 'block',
    width: '100%',
    padding: 0,
    cursor: 'pointer',
    lineHeight: 0,
    height: 96,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.surface.track,
    transition: 'border-color 120ms ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },
}));

const AttachmentThumbImage = styled('img')({
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
});

const AttachmentThumbFallback = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    height: 96,
    padding: theme.spacing(0.5),
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const AttachmentFileLink = styled(Link)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: theme.typography.size.md,
    wordBreak: 'break-word',
    marginBottom: theme.spacing(0.5),
}));

// SVG is not one of them: served from our own origin it would be a scriptable document, so an .svg attachment is treated as a file.
const IMAGE_NAME_PATTERN = /\.(png|jpe?g|gif|webp|avif)(\?|#|$)/i;

const DATE_PREFIX_PATTERN = /^\d{4}-\d{2}-\d{2}/;

const BUGHERD_PROJECT_ID_PATTERN = /\/projects\/(\d+)/;

const SELECTOR_HTML_CAPS = [500, 2000];

export const bugherdScreenshotUrl = (taskId, cardId, variant) =>
    taskId && cardId ? `${getApiUrl()}/bugherd/task/${taskId}/card/${cardId}/screenshot?variant=${variant}` : '';

const bugherdAttachmentUrl = (taskId, cardId, index) =>
    taskId && cardId ? `${bugherdScreenshotUrl(taskId, cardId, 'attachment')}&index=${index}` : '';

export const bugherdCoverUrl = (taskId, cardId, externalData) => {
    const data = externalData || {};
    if (data.screenshotUrl || data.screenshotPinnedUrl) return bugherdScreenshotUrl(taskId, cardId, 'plain');
    const files = Array.isArray(data.attachments) ? data.attachments : [];
    const index = files.findIndex((file) => file && (IMAGE_NAME_PATTERN.test(file.name || '') || IMAGE_NAME_PATTERN.test(file.url || '')));
    return index < 0 ? '' : bugherdAttachmentUrl(taskId, cardId, index);
};

const COMMENT_IMAGE_SRC = /\/?bugherd\/comment-image\/(\d+)/g;

export const withProxiedCommentImages = (comments, taskId, cardId) => {
    const list = Array.isArray(comments) ? comments : [];
    if (!taskId || !cardId) return list;
    return list.map((comment) => {
        if (typeof comment?.content !== 'string' || !comment.content.includes('bugherd/comment-image/')) return comment;
        return {
            ...comment,
            // The address below has this origin and this card baked into it, so the portable copy travels alongside for whoever edits the body.
            sourceContent: comment.content,
            content: comment.content.replace(COMMENT_IMAGE_SRC, (match, index) => `${bugherdScreenshotUrl(taskId, cardId, 'commentImage')}&index=${index}`),
        };
    });
};

// The position of the address in the text is counted exactly as routes/bugherd.js counts it, in both shapes a description comes in: image markdown as the board wrote it, and a
// Lexical image node once it has been through Pollux's editor.
const DESCRIPTION_IMAGE_SRC = /(!\[[^\]\n]*\]\(|"src"\s*:\s*")(https:\/\/files\.bugherd\.com\/[^\s")]+)/g;

export const withProxiedDescriptionImages = (description, taskId, cardId) => {
    const body = typeof description === 'string' ? description : '';
    if (!taskId || !cardId || !body.includes('files.bugherd.com')) return body;
    let index = -1;
    return body.replace(DESCRIPTION_IMAGE_SRC, (match, open) => {
        index += 1;
        return `${open}${bugherdScreenshotUrl(taskId, cardId, 'descriptionImage')}&index=${index}`;
    });
};

const text = (value) => (typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '');

const isSetValue = (value) => {
    const trimmed = text(value);
    return !!trimmed && !['not set', 'none', 'unset'].includes(trimmed.toLowerCase());
};

const isHttpUrl = (value) => /^https?:\/\//i.test(text(value));

const fileNameFromUrl = (value) => text(value).split('/').pop().split('?')[0] || 'image';

const extensionFromUrl = (value) => {
    const base = fileNameFromUrl(value);
    const dot = base.lastIndexOf('.');
    return dot > 0 ? base.slice(dot) : '.jpg';
};

const isDateLike = (value) => value instanceof Date || typeof value === 'number' || DATE_PREFIX_PATTERN.test(text(value));

const dayValue = (value) => (isDateLike(value) ? formatDate(value) : '');

const instantValue = (value) => (isDateLike(value) ? formatDateTime(value) : '');

const peopleLabel = (list) => {
    if (!Array.isArray(list)) return '';
    return list
        .map((person) => (person && typeof person === 'object' ? text(person.name) || text(person.email) : text(person)))
        .filter(Boolean)
        .join(', ');
};

const BugherdCardPanel = ({ externalData, taskId, cardId, onViewImage }) => {
    const [showUserAgent, setShowUserAgent] = useState(false);
    const [screenshotFailed, setScreenshotFailed] = useState(false);
    const [failedAttachmentKeys, setFailedAttachmentKeys] = useState({});

    const handleViewImage = useCallback((name, url) => {
        if (typeof onViewImage === 'function') onViewImage({ name, url });
    }, [onViewImage]);

    const data = externalData && typeof externalData === 'object' ? externalData : null;
    const browser = data?.browser && typeof data.browser === 'object' ? data.browser : {};

    // files.bugherd.com is on EasyPrivacy, so a hotlinked <img> is blocked for anyone running a blocker; both variants go through the proxy, which also falls back from pinned to
    // plain.
    const screenshot = useMemo(() => {
        const pinned = isHttpUrl(data?.screenshotPinnedUrl) ? text(data.screenshotPinnedUrl) : '';
        const plain = isHttpUrl(data?.screenshotUrl) ? text(data.screenshotUrl) : '';
        const hasImage = !!(pinned || plain);
        return {
            pinned,
            thumbUrl: hasImage ? bugherdScreenshotUrl(taskId, cardId, 'plain') : '',
            viewerUrl: hasImage ? bugherdScreenshotUrl(taskId, cardId, pinned ? 'pinned' : 'plain') : '',
            sourceUrl: pinned || plain,
        };
    }, [data, taskId, cardId]);

    // A non-image keeps its BugHerd link: opening one is a top-level navigation, which no blocker treats as third-party.
    const attachments = useMemo(() => {
        if (!Array.isArray(data?.attachments)) return [];
        return data.attachments
            .map((a, index) => ({ a, index }))
            .filter(({ a }) => a && typeof a === 'object' && isHttpUrl(a.url))
            .map(({ a, index }) => {
                const name = text(a.name) || 'Attachment';
                const url = text(a.url);
                return {
                    key: `${index}-${text(a.id)}`,
                    name,
                    url,
                    proxyUrl: bugherdAttachmentUrl(taskId, cardId, index),
                    isImage: IMAGE_NAME_PATTERN.test(name) || IMAGE_NAME_PATTERN.test(url),
                };
            });
    }, [data, taskId, cardId]);

    const imageAttachments = useMemo(() => attachments.filter((a) => a.isImage && !!a.proxyUrl), [attachments]);
    const fileAttachments = useMemo(() => attachments.filter((a) => !a.isImage || !a.proxyUrl), [attachments]);

    useEffect(() => {
        setScreenshotFailed(false);
        setFailedAttachmentKeys((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }, [data]);

    const handleThumbError = useCallback(() => setScreenshotFailed(true), []);
    const handleAttachmentThumbError = useCallback((key) => setFailedAttachmentKeys((prev) => ({ ...prev, [key]: true })), []);

    if (!data) return null;

    const taskNumber = text(data.bugherdTaskNumber);
    const viewUrl = isHttpUrl(data.viewUrl) ? text(data.viewUrl) : isHttpUrl(data.shareUrl) ? text(data.shareUrl) : '';
    const pageUrl = isHttpUrl(data.url) ? text(data.url) : '';
    const selectorPath = text(data.selectorPath);
    const selectorHtml = text(data.selectorHtml);
    const selectorHtmlTruncated = typeof data.selectorHtml === 'string' && SELECTOR_HTML_CAPS.includes(data.selectorHtml.length);
    const userAgent = text(browser.user_agent);
    const requesterName = text(data.requester?.name);
    const requesterEmail = text(data.requester?.email);
    const showRequesterName = !!requesterName && requesterName.toLowerCase() !== requesterEmail.toLowerCase();
    const assignees = peopleLabel(data.assignees);
    const tagNames = Array.isArray(data.tagNames) ? data.tagNames.map(text).filter(Boolean) : [];
    const dueAt = dayValue(data.dueAt);
    const syncedAt = instantValue(data.syncedAt);

    const thumbUrl = screenshotFailed ? '' : screenshot.thumbUrl;
    const screenshotUnavailable = !!screenshot.thumbUrl && screenshotFailed;
    const bugherdProjectId = (viewUrl.match(BUGHERD_PROJECT_ID_PATTERN) || [])[1] || '';
    const screenshotPinSuffix = screenshot.pinned ? '-with-pin' : '';
    const screenshotFileName = taskNumber
        ? `${bugherdProjectId ? `bugherd-${bugherdProjectId}-` : ''}task-${taskNumber}-screenshot${screenshotPinSuffix}${extensionFromUrl(screenshot.sourceUrl)}`
        : fileNameFromUrl(screenshot.sourceUrl);

    const hasEnvironment = !!(text(browser.browser) || text(browser.os) || text(browser.resolution) || text(browser.window_size) || userAgent);
    const hasElement = !!(selectorPath || selectorHtml);
    const hasReportFields = !!(requesterName || requesterEmail || assignees || tagNames.length > 0 || isSetValue(data.priority) || dueAt);
    const hasBody = !!(thumbUrl || screenshotUnavailable || pageUrl || hasReportFields || hasEnvironment || hasElement || attachments.length > 0 || syncedAt);

    if (!hasBody && !viewUrl) return null;

    return (
        <PanelRoot>
            <PanelHeader>
                <HeaderLeft>
                    <PanelTitle>BugHerd</PanelTitle>
                    {taskNumber && <TaskNumberChip size="small" variant="outlined" label={`#${taskNumber}`} />}
                </HeaderLeft>
                {viewUrl && (
                    <ExternalLink href={viewUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                        Open in BugHerd
                        <LinkIcon />
                    </ExternalLink>
                )}
            </PanelHeader>

            <PanelGrid $split={!!thumbUrl}>
                {thumbUrl && (
                    <Tooltip title="View screenshot">
                        <ScreenshotButton
                            type="button"
                            onClick={() => handleViewImage(screenshotFileName, screenshot.viewerUrl)}
                        >
                            <ScreenshotImage src={thumbUrl} alt="Screenshot of the reported page" onError={handleThumbError} />
                        </ScreenshotButton>
                    </Tooltip>
                )}
                {screenshotUnavailable && <ScreenshotFallback>Screenshot could not be loaded</ScreenshotFallback>}

                <Details>
                    {pageUrl && (
                        <Field>
                            <FieldLabel>Page</FieldLabel>
                            <FieldValue>
                                <Link href={pageUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                    {pageUrl}
                                </Link>
                            </FieldValue>
                        </Field>
                    )}

                    {hasReportFields && (
                        <>
                            {(requesterName || requesterEmail) && (
                                <Field>
                                    <FieldLabel>Reported by</FieldLabel>
                                    <FieldValue>
                                        {showRequesterName && requesterName}
                                        {!!requesterEmail && (
                                            <EmailLink href={`mailto:${requesterEmail}`} underline="hover">
                                                {requesterEmail}
                                            </EmailLink>
                                        )}
                                    </FieldValue>
                                </Field>
                            )}
                            {assignees && (
                                <Field>
                                    <FieldLabel>Assigned to</FieldLabel>
                                    <FieldValue>{assignees}</FieldValue>
                                </Field>
                            )}
                            {isSetValue(data.priority) && (
                                <Field>
                                    <FieldLabel>Priority</FieldLabel>
                                    <FieldValue>{text(data.priority)}</FieldValue>
                                </Field>
                            )}
                            {dueAt && (
                                <Field>
                                    <FieldLabel>Due</FieldLabel>
                                    <FieldValue>{dueAt}</FieldValue>
                                </Field>
                            )}
                            {tagNames.length > 0 && (
                                <Field>
                                    <FieldLabel>Tags</FieldLabel>
                                    <TagRow>
                                        {tagNames.map((tag, index) => (
                                            <Chip key={`${index}-${tag}`} size="small" variant="outlined" label={tag} />
                                        ))}
                                    </TagRow>
                                </Field>
                            )}
                        </>
                    )}

                    {hasEnvironment && (
                        <>
                            <SubLabel>Environment</SubLabel>
                            {text(browser.browser) && (
                                <Field>
                                    <FieldLabel>Browser</FieldLabel>
                                    <FieldValue>{text(browser.browser)}</FieldValue>
                                </Field>
                            )}
                            {text(browser.os) && (
                                <Field>
                                    <FieldLabel>OS</FieldLabel>
                                    <FieldValue>{text(browser.os)}</FieldValue>
                                </Field>
                            )}
                            {text(browser.resolution) && (
                                <Field>
                                    <FieldLabel>Screen resolution</FieldLabel>
                                    <FieldValue>{text(browser.resolution)}</FieldValue>
                                </Field>
                            )}
                            {text(browser.window_size) && (
                                <Field>
                                    <FieldLabel>Window size</FieldLabel>
                                    <FieldValue>{text(browser.window_size)}</FieldValue>
                                </Field>
                            )}
                            {userAgent && (
                                <>
                                    <RevealToggle component="button" type="button" underline="hover" onClick={() => setShowUserAgent((prev) => !prev)}>
                                        {showUserAgent ? 'Hide user agent' : 'Show user agent'}
                                    </RevealToggle>
                                    <Collapse in={showUserAgent} unmountOnExit>
                                        <CodeBlock component="pre">{userAgent}</CodeBlock>
                                    </Collapse>
                                </>
                            )}
                        </>
                    )}

                    {hasElement && (
                        <>
                            <SubLabel>Element</SubLabel>
                            {selectorPath && (
                                <>
                                    <CodeLabel>CSS path</CodeLabel>
                                    <CodeBlock component="pre">{selectorPath}</CodeBlock>
                                </>
                            )}
                            {selectorHtml && (
                                <>
                                    <CodeLabel>{selectorHtmlTruncated ? 'HTML (truncated)' : 'HTML'}</CodeLabel>
                                    <CodeBlock component="pre">{selectorHtml}</CodeBlock>
                                </>
                            )}
                        </>
                    )}

                    {attachments.length > 0 && (
                        <>
                            <SubLabel>BugHerd attachments</SubLabel>
                            {imageAttachments.length > 0 && (
                                <AttachmentGrid>
                                    {imageAttachments.map((attachment) =>
                                        failedAttachmentKeys[attachment.key] ? (
                                            <Tooltip key={attachment.key} title={attachment.name}>
                                                <AttachmentThumbFallback>Preview could not be loaded</AttachmentThumbFallback>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip key={attachment.key} title={attachment.name}>
                                                <AttachmentThumbButton type="button" onClick={() => handleViewImage(attachment.name, attachment.proxyUrl)}>
                                                    <AttachmentThumbImage src={attachment.proxyUrl} alt={attachment.name} onError={() => handleAttachmentThumbError(attachment.key)} />
                                                </AttachmentThumbButton>
                                            </Tooltip>
                                        )
                                    )}
                                </AttachmentGrid>
                            )}
                            {fileAttachments.map((attachment) => (
                                <AttachmentFileLink key={attachment.key} href={attachment.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                    <InsertDriveFile fontSize="small" color="action" />
                                    {attachment.name}
                                </AttachmentFileLink>
                            ))}
                        </>
                    )}

                    {syncedAt && (
                        <Field>
                            <FieldLabel>Last synced</FieldLabel>
                            <FieldValue>{syncedAt}</FieldValue>
                        </Field>
                    )}
                </Details>
            </PanelGrid>
        </PanelRoot>
    );
};

export default BugherdCardPanel;
